import React, { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Sparkles } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { IntakeStrategyEditor, cleanStrategy } from './IntakeStrategyEditor';
import { CourtEvidence } from './CourtConfirmation';

const show = (s, v) => (s.field === 'sala_numero' ? `Sala ${v}` : String(v ?? '—'));

// Review what the AI proposes after new documents: accept each data change and approve (or not) the new strategy version
export const PendingUpdateReview = ({ matter, update, onDone }) => {
  const { api } = useAuth();
  const [fields, setFields] = useState([]);
  const [strategy, setStrategy] = useState(update.strategy || {});
  const [approved, setApproved] = useState(false);
  const [busy, setBusy] = useState(null);
  const toggle = (f) => setFields(fields.includes(f) ? fields.filter((x) => x !== f) : [...fields, f]);

  const apply = async () => {
    setBusy('apply');
    try {
      const { data } = await api.post(`/crm/matters/${matter.id}/pending-update/apply`, { update_id: update.id, fields, strategy: approved ? cleanStrategy(strategy) : null });
      toast.success(`${data.applied_fields.length} dato(s) actualizado(s)${data.strategy_updated ? ' y nueva versión de estrategia aprobada' : ''}`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo aplicar'); } finally { setBusy(null); }
  };
  const discard = async () => {
    setBusy('discard');
    try { await api.delete(`/crm/matters/${matter.id}/pending-update`); toast.success('Propuesta descartada; los documentos siguen en el expediente'); onDone(); }
    catch { toast.error('No se pudo descartar'); } finally { setBusy(null); }
  };

  return (
    <div data-testid="pending-update-review" className="space-y-3 rounded-xl border border-sky-500/30 bg-sky-500/5 p-3">
      <p className="flex items-center gap-1.5 text-sm font-semibold text-sky-200"><Sparkles className="h-4 w-4" />Propuesta por documentos nuevos: {update.files.join(', ')}</p>
      {update.field_suggestions.length > 0 ? (
        <div className="space-y-2">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Cambios sugeridos en los datos del caso</p>
          {update.field_suggestions.map((s) => (
            <label key={s.field} data-testid={`pending-field-${s.field}`} className="flex items-start gap-2 rounded-lg border border-slate-700 bg-slate-900/60 p-2 text-xs">
              <input type="checkbox" data-testid={`pending-field-accept-${s.field}`} checked={fields.includes(s.field)} onChange={() => toggle(s.field)} className="mt-0.5 accent-sky-500" />
              <div className="min-w-0">
                <b className="text-slate-200">{s.label}:</b> <span className="text-slate-500 line-through">{s.actual ? show(s, s.actual).slice(0, 120) : 'vacío'}</span> → <span className="text-sky-100">{show(s, s.value).slice(0, 400)}</span>
                {s.motivo && <span className="block text-[11px] text-slate-400">{s.motivo}</span>}
                {s.field === 'court' && <CourtEvidence evidence={s.court_review?.evidence} testid="pending-court" />}
              </div>
            </label>
          ))}
        </div>
      ) : <p className="text-xs text-slate-400">Los documentos no respaldan cambios en los datos del caso.</p>}
      {update.cambios.length > 0 && (
        <div data-testid="pending-strategy-changes">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Qué cambia en la estrategia{update.base_version ? ` (respecto de la versión ${update.base_version})` : ''}</p>
          <ul className="list-disc pl-4 text-xs text-slate-300">{update.cambios.map((c, i) => <li key={i}>{c}</li>)}</ul>
        </div>
      )}
      <IntakeStrategyEditor value={strategy} onChange={setStrategy} approved={approved} onApprove={setApproved} />
      <p className="text-[11px] text-slate-500">Si no apruebas la nueva versión, la estrategia vigente se mantiene.</p>
      <div className="flex flex-wrap gap-2">
        <button type="button" data-testid="pending-update-apply" disabled={!!busy || (!fields.length && !approved)} onClick={apply} className="flex items-center gap-1.5 rounded-full bg-sky-500 px-4 py-1 text-xs font-semibold text-slate-950 disabled:opacity-40">
          {busy === 'apply' && <Loader2 className="h-3 w-3 animate-spin" />}Aplicar lo seleccionado
        </button>
        <button type="button" data-testid="pending-update-discard" disabled={!!busy} onClick={discard} className="rounded-full border border-slate-600 px-4 py-1 text-xs text-slate-300">Descartar propuesta</button>
      </div>
    </div>
  );
};
